/**
 * Global Styles for Lobby2.0
 * Replaces index.css with styled-components global styles
 */

import { createGlobalStyle } from 'styled-components';
import type { Theme } from './theme';

export const GlobalStyles = createGlobalStyle<{ theme: Theme }>`
  /* Fonts are loaded dynamically from assets server via FontLoader service */
  
  // Prevents zooming
  html,
  body,
  * {
    touch-action: pan-y pan-x;
  }

  /* CSS Variables for game cards and fonts */
  :root {    
    --border-radius-for-game-cards: 10px;
    --jp-bottom-band-height: 20px;
    --game-info-bar-font-size: 12px;
    --game-max-bet-text-font-size: 14px;
    --game-min-max-bet-color: #fbdf19;
    --navigator-selected-color: #fce34c;
    --piliot-green: #3CFF00;
    --eco-green: #28A809;
    --white-color: #ffffff;
    --ghost-white-color: #f9f9f9;
    --black: #000;
    --cardBackground: #1a1a1a;
    --dark-grey: #333333;
    --appBackground: #2e2e2e;
    --light-grey: #666666;
    --border-light: #555555;
    --header-background-color: #505050;
    --ash-grey: #b0b0b0;
    --settings-modal-text-color: #cfcfcf;
    --settings-icon-color: #9D9D9D;
    --jp-panel-linked-text-color: #ffe1e1;
    --jp-bottom-band-font-size: 17px;
    --header-title-font-size: 14px;
    --header-all-game-font-size: 12px;
    --grid-left-right-padding: 20px;
    --header-title-height-multiview: 50px;
    --header-title-height-singleview: 40px;
    --section-body-bottom-margin: 5px;

    --status-bar-height: 44px;
    --navigation-bar-height: 64px;
    /* Space the fixed Navigation/StatusBar cover over the scrolling content; scrollers
       reserve it at the end of their content so hiding the bars reveals content rather
       than a gap. The safe-area term is what iOS adds below the bars for the home
       indicator — without it the last row ends up under the navigation on iPhone. */
    --lobby-bars-height: calc(
      var(--status-bar-height) + var(--navigation-bar-height) + env(safe-area-inset-bottom, 0px)
    );

    --filter-active-icon: invert(48%) sepia(95%) saturate(627%) hue-rotate(65deg) brightness(126%) contrast(113%);

    --game-card-aspect-ratio: 1 / 1.37804878;
    --game-card-height-multiplier: 1.37804878;

    @media (orientation: landscape) and (min-width: 576px) {
      --navigation-bar-height: 55px;
    }

    --game-grid-padding: 8px;

    --most-played-num-image-width: 52px;
    --most-played-num-image-height: 52px;
    
    /* Old lobby CSS variables for working navigation */
    --navigator-height: 70px;
    --info-line-height: 40px;
    --navigator-cell-height: 60px;
    --tournament-navigator-height: 64px;
    --general-padding: 8px;

    ${({ theme }) => theme.media.landscape} {
      --tournament-navigator-height: 55px;
    }

    --lobby-container-width: 100vw;
    --lobby-container-height: 100vh;
    /* Modern browsers and iOS Safari */
    @supports (height: 100dvh) {
      --lobby-container-height: 100dvh;
    }
    
    /* Ingame mode dimensions - set dynamically by Game Client via setMountElementDimensions() */
    --lobby-ingame-left-right-offset: 0px;
    --lobby-ingame-top-bottom-offset: 0px;
    --lobby-ingame-popup-title-bar-height: 30px;
    --main-lobby-border-radius: 10px;
    
    /* Font families from old lobby */
    --font-family-primary: 'Futura PT', -apple-system, BlinkMacSystemFont, 'Roboto', 'Helvetica Neue', 'Arial', 'sans-serif';
    --font-family-secondary: 'Futura PT Cond', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    --font-family-tertiary: 'Kranto', Impact, "Arial Black", sans-serif;
    --font-family-quaternary: 'San Francisco Bold', 'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif;
    --font-family-numeric: 'Numeric';
    
    /* Mobile-first responsive scaling */
    --game-card-width: 132px;
    --game-card-height: 182px;
    --navigation-height: 70px;
    --grid-columns: 2;
    --grid-gap: 8px;
    --grid-padding: 16px;
    --status-bar-side-padding: 5px;
    
    /* Touch target sizes */
    --touch-target-min: 44px;
    --touch-target-comfortable: 48px;
    
    /* Tournament card dimensions - responsive */
    --grid-cell-width-tournaments: 240px;
    
    
    @media (min-width: ${({ theme }) => theme.breakpoints.xs}) {
      --most-played-num-image-width: 72px;
      --most-played-num-image-height: 72px;
      --jp-bottom-band-height: 20px;
    }

    @media (min-width: ${({ theme }) => theme.breakpoints.xs1}) {
      --header-title-font-size: 16px;
    }

    @media (min-width: ${({ theme }) => theme.breakpoints.xs4}) {
      --game-card-width: 140px;
      --game-card-height: 193px;
      --navigation-height: 75px;
      --grid-columns: 3;
      --grid-padding: 20px;

      --game-info-bar-font-size: 12px;
      --jp-bottom-band-font-size: 10px;
      --navigator-height: 75px;
      --info-line-height: 45px;
      --navigator-cell-height: 65px;
      --status-bar-side-padding: 15px;
      --jp-bottom-band-height: 24px;
    }


    @media (min-width: ${({ theme }) => theme.breakpoints.sm}) {
      --most-played-num-image-width: 82px;
      --most-played-num-image-height: 82px;
      --jp-bottom-band-height: 24px;
    }
    
    @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
      --game-card-width: 164px;
      --game-card-height: 226px;
      --navigation-height: 80px;
      --grid-columns: 4;
      --grid-padding: 24px;
      --jp-bottom-band-height: 24px;
      --game-info-bar-font-size: 12px;
      --jp-bottom-band-font-size: 11px;
      --navigator-height: 80px;
      --info-line-height: 50px;
      --navigator-cell-height: 70px;
      --section-body-bottom-margin: 10px;
    }
    
    @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
      --game-card-width: 200px;
      --game-card-height: 276px;
      --grid-columns: 5;
      --grid-gap: 10px;
      --grid-padding: 32px;
      --jp-bottom-band-height: 34px;
      --game-info-bar-font-size: 14px;
      --game-max-bet-text-font-size: 16px;
      --jp-bottom-band-font-size: 17px;
      --grid-cell-width-tournaments: 296px;
      --header-title-font-size: 24px;
      --header-title-height-multiview: 60px;
      --header-title-height-singleview: 60px;
      --header-all-game-font-size: 18px;
    }

    ${({ theme }) => theme.media.lgUp} { 
      --most-played-num-image-width: 160px;
      --most-played-num-image-height: 160px;
    }

    @media (min-width: ${({ theme }) => theme.breakpoints.xl}) {
      --game-card-width: 246px;
      --game-card-height: 339px;
      --jp-bottom-band-height: 34px;
    }
  }

  /* Seasonal branding. useSeasonalPalette stamps data-seasonal-branding on <html> from the
     seasonalBranding flag so CSS can key off it, portals (modals) included. Neither seasonal
     branding ships a palette: the lobby colours stay stock and only the dressing changes
     (halloween: bats, webs, fog, intro; christmas: snow, fairy lights, frost, intro and the
     amber section icons pinned in SectionHeader.module.css). */

  /* Reset and base styles */
  * {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  html {
    /* iOS Safari viewport fix */
    -webkit-text-size-adjust: 100%;
    text-size-adjust: 100%;
    
    /* Font smoothing */
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    
    /* Touch optimizations */
    -webkit-tap-highlight-color: transparent;
    -webkit-touch-callout: none;
    
    /* Mobile-first font sizing */
    font-size: 14px;
    line-height: 1.6;
    
    @media (min-width: 430px) {
      font-size: 15px;
    }
    
    @media (min-width: 768px) {
      font-size: 16px;
    }
    
    @media (min-width: 1024px) {
      font-size: 17px;
    }
  }

  body {
    font-family: var(--font-family-primary);
    background: ${({ theme }) => theme.colors.appBackground};
    color: ${({ theme }) => theme.colors.textPrimary};
    
    /* Mobile optimizations */
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
    
    /* iOS touch scrolling optimization */
    -webkit-overflow-scrolling: touch;
    
    /* Fixed body for iframe usage - no scrolling at body level */
    overflow: hidden;
    margin: 0;
    padding: 0;
    position: fixed;
    width: 100%;
    
    /* Desktop uses viewport units */
    @media (min-width: 769px) {
      height: 100vh;
      height: 100dvh;
    }
    
    /* Mobile Safari iframe-safe height */
    @media (max-width: 768px) {
      height: var(--app-height, 100vh);
    }
    
    /* iOS Safari address bar fix */
    @supports (-webkit-touch-callout: none) {
      @media (max-width: 768px) {
        height: var(--app-height, 100vh);
      }
    }
  }

  #root, #react-lobby-root {
    width: 100%;
    overflow: hidden;
    position: fixed;
    top: 0;
    left: 0;
    
    /* Desktop uses viewport units */
    @media (min-width: 769px) {
      height: 100vh;
      height: 100dvh;
    }
    
    /* Mobile Safari iframe-safe height */
    @media (max-width: 768px) {
      height: var(--app-height, 100vh);
    }
    
    /* iOS Safari address bar fix */
    @supports (-webkit-touch-callout: none) {
      @media (max-width: 768px) {
        height: var(--app-height, 100vh);
      }
    }
  }

  /* Text and headings */
  h1, h2, h3, h4, h5, h6 {
    font-weight: ${({ theme }) => theme.typography.fontWeights.bold};
    line-height: ${({ theme }) => theme.typography.lineHeights.tight};
    margin-bottom: ${({ theme }) => theme.spacing['4']};
  }

  h1 {
    font-size: ${({ theme }) => theme.typography.fontSizes['4xl']};
  }

  h2 {
    font-size: ${({ theme }) => theme.typography.fontSizes['3xl']};
  }

  h3 {
    font-size: ${({ theme }) => theme.typography.fontSizes['2xl']};
  }

  h4 {
    font-size: ${({ theme }) => theme.typography.fontSizes.xl};
  }

  h5 {
    font-size: ${({ theme }) => theme.typography.fontSizes.lg};
  }

  h6 {
    font-size: ${({ theme }) => theme.typography.fontSizes.md};
  }

  p {
    margin-bottom: ${({ theme }) => theme.spacing['4']};
    line-height: ${({ theme }) => theme.typography.lineHeights.normal};
  }

  /* Links */
  a {
    color: ${({ theme }) => theme.colors.primary};
    text-decoration: none;
    transition: color ${({ theme }) => theme.transitions.duration.fast} ${({ theme }) =>
    theme.transitions.easing.easeInOut};

    &:hover {
      color: ${({ theme }) => theme.colors.primaryHover};
    }

    &:focus {
      outline: 2px solid ${({ theme }) => theme.colors.primary};
      outline-offset: 2px;
    }
  }

  /* Buttons */
  button {
    font-family: inherit;
    cursor: pointer;
    border: none;
    background: none;
    transition: all ${({ theme }) => theme.transitions.duration.fast} ${({ theme }) =>
    theme.transitions.easing.easeInOut};
    
    /* Touch optimizations */
    -webkit-tap-highlight-color: transparent;
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    user-select: none;
    
    /* Minimum touch target size */
    min-height: var(--touch-target-min);
    min-width: var(--touch-target-min);

    &:disabled {
      cursor: not-allowed;
      opacity: 0.6;
    }

    &:focus-visible {
      outline: none;
    }
    
    /* Touch feedback on mobile */
    @media (hover: none) and (pointer: coarse) {
      &:active {
        transform: scale(0.95);
        transition: transform 0.1s ease;
      }
    }
  }

  /* Inputs */
  input, textarea, select {
    font-family: inherit;
    font-size: ${({ theme }) => theme.typography.fontSizes.md};
    border: 1px solid ${({ theme }) => theme.colors.border};
    border-radius: ${({ theme }) => theme.borderRadius.md};
    padding: ${({ theme }) => theme.spacing['3']};
    background: ${({ theme }) => theme.colors.cardBackground};
    color: ${({ theme }) => theme.colors.textPrimary};
    transition: border-color ${({ theme }) => theme.transitions.duration.fast} ${({ theme }) =>
    theme.transitions.easing.easeInOut};

    &:focus {
      outline: none;
      border-color: ${({ theme }) => theme.colors.primary};
      box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primary}25;
    }

    &::placeholder {
      color: ${({ theme }) => theme.colors.textMuted};
    }
  }

  /* Images */
  img {
    max-width: 100%;
    height: auto;
    display: block;
  }

  /* Lists */
  ul, ol {
    margin-bottom: ${({ theme }) => theme.spacing['4']};
    padding-left: ${({ theme }) => theme.spacing['6']};
  }

  li {
    margin-bottom: ${({ theme }) => theme.spacing['2']};
  }

  /* Scrollbars (Mobile-first) */
  ::-webkit-scrollbar {
    width: 4px;
    height: 4px;
    
    @media (min-width: 768px) {
      width: 8px;
      height: 8px;
    }
  }

  ::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 2px;
  }

  ::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.3);
    border-radius: 2px;
    
    &:hover {
      background: rgba(255, 255, 255, 0.5);
    }
  }

  /* Firefox scrollbars */
  * {
    scrollbar-width: thin;
    scrollbar-color: rgba(255, 255, 255, 0.3) rgba(255, 255, 255, 0.1);
    
    /* Touch scrolling optimization */
    -webkit-overflow-scrolling: touch;
  }

  /* Animations */
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @keyframes slideIn {
    from {
      transform: translateY(-10px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }

  @keyframes pulse {
    0%, 100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }

  /* Utility classes */
  .fade-in {
    animation: fadeIn ${({ theme }) => theme.transitions.duration.normal} ${({ theme }) =>
    theme.transitions.easing.easeOut};
  }

  .slide-in {
    animation: slideIn ${({ theme }) => theme.transitions.duration.normal} ${({ theme }) =>
    theme.transitions.easing.easeOut};
  }

  .pulse {
    animation: pulse ${({ theme }) => theme.transitions.duration.slow} ${({ theme }) =>
    theme.transitions.easing.easeInOut} infinite;
  }

  /* Responsive design helpers */
  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  /* Game specific styles */
  .game-card {
    transition: transform ${({ theme }) => theme.transitions.duration.fast} ${({ theme }) =>
    theme.transitions.easing.easeOut};
    
    /* Hover effects only on non-touch devices */
    @media (hover: hover) and (pointer: fine) {
      &:hover {
        transform: translateY(-2px);
      }
    }
    
    /* Touch feedback on mobile */
    @media (hover: none) and (pointer: coarse) {
      &:active {
        transform: scale(0.98);
        transition: transform 0.1s ease;
      }
    }
  }

  .jackpot-glow {
    animation: jackpotPulse 2s ease-in-out infinite alternate;
  }

  @keyframes jackpotPulse {
    from {
      box-shadow: 0 0 10px ${({ theme }) => theme.colors.jackpotGold}40;
    }
    to {
      box-shadow: 0 0 20px ${({ theme }) => theme.colors.jackpotGold}80;
    }
  }

  /* Touch-specific optimizations */
  @media (hover: none) and (pointer: coarse) {
    /* Remove hover effects on touch devices */
    .game-card:hover {
      transform: none;
    }
    
    /* Improve touch scrolling performance */
    * {
      -webkit-overflow-scrolling: touch;
    }
  }
  
  /* High DPI display optimizations */
  @media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
    * {
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
    }
  }
  
  /* Reduce motion for accessibility */
  @media (prefers-reduced-motion: reduce) {
    *, *::before, *::after {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.01ms !important;
      scroll-behavior: auto !important;
    }
  }
  
  /* iOS Safari specific fixes */
  @supports (-webkit-touch-callout: none) {
    /* Prevent elastic scrolling */
    .ios-scroll-fix {
      overflow: hidden;
      position: fixed;
      width: 100%;
      height: 100%;
    }
  }

  /* Samsung Internet Browser dark mode defense */
  /* Samsung's Night Mode applies CSS filter inversions that ignore color-scheme meta tags. */
  /* Targeted via data attribute set by useSamsungDarkModeDefense hook (Samsung-only). */
  html[data-samsung-dark-mode="true"],
  html[data-samsung-dark-mode="true"] body {
    color-scheme: light only !important;
    filter: none !important;
    -webkit-filter: none !important;
    background-color: ${({ theme }) => theme.colors.appBackground} !important;
    color: ${({ theme }) => theme.colors.textPrimary} !important;
  }

  html[data-samsung-dark-mode="true"] img,
  html[data-samsung-dark-mode="true"] svg,
  html[data-samsung-dark-mode="true"] video,
  html[data-samsung-dark-mode="true"] canvas,
  html[data-samsung-dark-mode="true"] picture {
    filter: none !important;
    -webkit-filter: none !important;
    mix-blend-mode: normal !important;
  }

  /* Print styles */
  @media print {
    * {
      background: white !important;
      color: black !important;
      box-shadow: none !important;
    }
  }

  /* ========== IFRAME LOBBY STYLES (FROM OLD LOBBY) ========== */
  
  /* Fullscreen lobby mount element styles */
  .egt_react_lobby_fullscreen {
    width: 100vw;
    top: 0;
    left: 0;
  }

  /* Critical iframe styles for fullscreen mode */
  .egt_react_lobby_fullscreen > iframe {
    width: 100vw !important;
    /* dvh handles resizing from fullscreen on ipads */
    height: 100vh !important;
    height: 100dvh !important;
    border: none !important;
    position: fixed !important;
    top: 0 !important;
    left: 0 !important;
    z-index: 10001 !important;
    margin: 0 !important;
    padding: 0 !important;
  }



  .egt_react_lobby_fullscreen [class*="NavigationWrapper"] {
    position: fixed;
    /* Use normal height for fullscreen mode */
    height: auto !important;
  }

  .egt_react_lobby_fullscreen .egt_status_container {
    position: fixed;
  }

  /* Ingame lobby mount element styles */
  .egt_react_lobby_ingame {
    position: fixed;
  }

  /* No StatusBar is rendered ingame, so only the navigation covers content. Keyed on
     our own data-open-type attribute rather than the mount class above: that class
     lives on the HOST page's element in Game Client, and custom properties don't
     cross the iframe boundary. */
  [data-open-type='ingame'] {
    --lobby-bars-height: calc(var(--navigation-bar-height) + env(safe-area-inset-bottom, 0px));
  }


  /* Ingame mode (iframe) lobby container padding */
  .egt_react_lobby_ingame [class*="lobbyContainer"] {
    padding-bottom: 156px !important; /* The tested height that works */
  }

  .egt_react_lobby_ingame [class*="NavigationWrapper"] {
    position: absolute;
    /* Use the 156px height fix for ingame mode (iframe) */
    height: 156px !important;
  }

  .egt_react_lobby_ingame .egt_status_container {
    position: absolute;
  }

  /* Generic iframe styles for ingame mode */
  .egt_react_lobby_ingame iframe {
    width: 100% !important;
    height: 100% !important;
    border: none !important;
    position: absolute !important;
  }

  /* Lobby root element styles */
  #react-lobby-root {
    width: 100%;
    left: 0;
    top: 0;
  }

  #react-lobby-root.egt_react_lobby_mobile {
    height: 99%;
  }

  /* Remote lobby root element styles (used by Game Client) */
  #react-lobby-remote-root {
    /* width: 100vw !important;
    height: 100vh !important;
    height: 100dvh !important; 
    left: 0 !important;
    top: 0 !important;
    position: fixed !important;
    z-index: 10000 !important;
    overflow: hidden !important; */

    /* display: block; */
    /* position: fixed; */
  }

  #react-lobby-remote-root.egt_react_lobby_mobile {
    height: 99%;
  }

  /* Background blocker styles */
  .egtd_background_blocker {
    width: 100vw;
    height: 100vh;
    position: fixed;
    top: 0px;
    left: 0px;
  }

  /* Mobile device specific adjustments */
  .egt_react_lobby_mobile iframe {
    /* Prevent iOS address bar issues */
    -webkit-overflow-scrolling: touch;
  }

  /* Desktop device specific adjustments */
  .egt_react_lobby_desktop {
    /* Desktop specific styling if needed */
  }

  /* Fallback iframe styles - apply to any iframe in remote root */
  #react-lobby-remote-root iframe {
    width: 100vw;
    height: 100dvh;
    border: none;
    position: fixed;
    top: 0;
  }

  /* ========== END IFRAME LOBBY STYLES ========== */

  /* ========== PERFORMANCE OPTIMIZATIONS ========== */
  
  /* Enable GPU acceleration globally for smooth transitions */
  .sectionContainer,
  .gamesGrid,
  .lobbyContainer {
    transform: translateZ(0);
    backface-visibility: hidden;
    perspective: 1000px;
  }

  /* Optimize view switching performance */
  [data-view-switching="true"] * {
    transition: none !important;
    animation: none !important;
  }

  /* Contain layout calculations for better performance */
  .sectionContainer {
    contain: layout style paint;
  }

  /* Optimize single view sections */
  [data-view="single"] .sectionContainer {
    contain: strict;
    will-change: contents;
  }

  /* Game containers optimization */
  [data-game-container] {
    contain: layout style paint;
    will-change: transform;
  }

  /* Optimize image rendering */
  .game-image {
    image-rendering: -webkit-optimize-contrast;
    image-rendering: crisp-edges;
    contain: layout style paint;
  }

  /* Reduce paint areas during scrolling */
  .gamesGrid {
    contain: layout style paint;
    will-change: scroll-position;
  }

  /* Optimize hover states */
  @media (hover: hover) {
    [data-game-container]:hover {
      transform: translateZ(0) scale(1.02);
      will-change: transform;
    }
  }

  /* Jackpot animation optimization */
  [data-jackpot-value] {
    contain: layout style;
    will-change: contents;
  }

  /* Navigation optimization */
  [data-navigation] {
    contain: layout style paint;
    transform: translateZ(0);
  }

  /* Prevent layout thrashing */
  .lobbyContainer {
    isolation: isolate;
  }

  /* iOS specific GPU acceleration */
  @supports (-webkit-touch-callout: none) {
    .sectionContainer,
    .gamesGrid,
    .lobbyContainer {
      -webkit-transform: translate3d(0, 0, 0);
    }
  }

  /* High performance scrolling */
  * {
    -webkit-overflow-scrolling: touch;
    /* NOTE: overscroll-behavior-y: contain was removed from here.
       Chrome 144+ treats overflow:hidden elements as scroll containers,
       causing overscroll-behavior:contain to trap scroll events.
       Apply overscroll-behavior only to actual scrollable containers. */
  }

  /* Disable expensive effects during rapid navigation */
  [data-fast-switching] * {
    box-shadow: none !important;
    filter: none !important;
    backdrop-filter: none !important;
  }
  /* CSS Variables for jackpot system - matching old lobby dimensions */
  :root {
    /*Game Grid *?
    --single-section-margin-top: 16px;
    
    /* Panel dimensions (matching old lobby) */
    --jp-panel-width: 425px;
    --jp-panel-height: 290px;

    --tr-panel-width: 372px;
    --tr-panel-height: 330px;

    @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
      --tr-panel-width: 445px;
    }

    /* Level dimensions (matching old lobby standard) */
    --jp-grand-width: 333px;
    --jp-major-width: 274px;
    --jp-minor-width: 155px;
    --jp-mini-width: 155px;

    /* Level heights - the values are used for Mystery and High Cash JPs.
       The other JPs overrides these values. */
    --jp-grand-height: 76.5px;
    --jp-major-height: 62.91px;
    --jp-minor-height: 35.5px;
    --jp-mini-height: 35.5px;

    ${({ theme }) => theme.media.lgUp} {
      --jp-grand-height: 83.8px;
      --jp-major-height: 68.94px;
      --jp-minor-height: 38.75px;
      --jp-mini-height: 38.75px;
    }

    /* Level Font Size - these values have overrides in StandartJackpot.css and the css files of the jackpot variants */
    --jp-grand-font-size: 40px;
    --jp-major-font-size: 35px;
    --jp-minor-font-size: 20px;
    --jp-mini-font-size: 20px;

    /* Level Currency Font Size */
    --jp-grand-currency-font-size: 15px;
    --jp-major-currency-font-size: 12px;
    --jp-minor-currency-font-size: 7px;
    --jp-mini-currency-font-size: 7px;

    /* Jackpot type colors */
    --jp-mystery-color: linear-gradient(180deg, #046ce5, #0040b9);
    --jp-highcash-color: linear-gradient(180deg, #6238cc, #472797);
    --jp-belllink-color: linear-gradient(180deg, #ff6b00, #ff4500);
    --jp-godsandkings-color: linear-gradient(180deg, #0c6e17, #075710);
    --jp-vip-godsandkings-color: linear-gradient(180deg, #a00500, #7c0500);
    --jp-instantlink-color: linear-gradient(180deg, #ffd700, #ffb300);

    --jp-level-value-align: center;
    --jp-level-value-padding-right: 5px;
    --jp-currency-margin-left: 10px;
    --jp-currency-position: static;
    --jp-level-padding-top: 0px;

    /* Typography */
    --jp-font-family: var(--font-family-numeric, 'Roboto', sans-serif);
    --jp-currency-font-family: 'Kranto';
    --jp-value-color: #fce34c;
    --jp-value-font-weight: 100;
    --jp-currency-color: #ffffff;
  }
  /* ========== END PERFORMANCE OPTIMIZATIONS ========== */
`;
